<!-- Localized -->
<template>
  <div class="flex-1 flex flex-col max-h-full">
    <sub-header
      :showBtn="showCreateButton"
      :handleClick="createCaseWorkflow"
      :buttonText="$t('pages.workflow_engine.create_workflow')"
    />
    <div
      class="flex flex-1 flex-col flex-grow max-w-full overflow-auto scroll-bar"
    >
      <v-table
        id="caseStatusTable"
        :mode="'records'"
        :columns="columns"
        :rows="translatedItems"
        @handleClone="handleCloneWorkflow($event)"
        :isLoading="listLoader"
        :pagination-options="paginationOptions"
        :total-rows="getTotalRows"
        @column-filter="onColumnFilters($event)"
        @page-change="onPageChange"
        @filters-clear="clearFilters"
        :filters="filterTerm"
        :filterOptions="filterOptions"
        sortingEnabled
      >
        <template #actions="customData">
          <div class="flex justify-around">
            <button
              class="h-6 w-4 mx-2 mt-1 disabled:opacity-50"
              @click="handleCloneWorkflow(customData.customData)"
              v-tippy
              :content="'Clone'"
            >
              <font-awesome-icon
                icon="clone"
                class="text-blue-600 text-lg fa-2x"
                :class="
                  customData.customData
                    ? 'text-primary hover:text-dvbrandhoveron hover:underline cursor-pointer'
                    : 'text-gray-500'
                "
              />
            </button>
            <router-link
              :to="{
                name: 'Workflow Engine Details',
                params: {
                  workflowId: customData?.customData?.id,
                  action: 'edit',
                  type: 'check',
                },
              }"
            >
              <button
                class="h-8 w-4 disabled:opacity-50 mx-2"
                v-tippy
                :content="'Edit'"
                :disabled="!checkPermission('workflow.edit')"
              >
                <font-awesome-icon
                  icon="edit"
                  class="text-blue-600 text-lg fa-3x"
                  :class="
                    customData.customData
                      ? 'text-primary hover:text-dvbrandhoveron hover:underline cursor-pointer'
                      : 'text-gray-500'
                  "
                />
              </button>
            </router-link>
            <router-link
              :to="{
                name: 'Workflow Engine Details',
                params: {
                  workflowId: customData?.customData?.id,
                  action: 'view',
                  type: 'check',
                },
              }"
            >
              <button
                class="h-8 w-4 disabled:opacity-50 mx-2"
                v-tippy
                :content="'View'"
              >
                <font-awesome-icon
                  icon="eye"
                  class="text-blue-600 text-lg fa-3x"
                  :class="
                    customData.customData
                      ? 'text-primary hover:text-dvbrandhoveron hover:underline cursor-pointer'
                      : 'text-gray-500'
                  "
                />
              </button>
            </router-link>
          </div>
        </template>
      </v-table>
    </div>
    <modalContent ref="clone-check-workflow-modal" :max-width="370" height="75%" name="cloneCaseWorkflow" :title="$t('pages.clone.clone_workflow', { value: 'Check' })" @close="closeCheckWorkFlow" :showFooter="false">
        <template #content>
        <div class="flex-1">
          <div class="flex flex-col flex-wrap max-w-xs">
            <FormulateForm #default="{ hasErrors }">
              <FormulateInput
                type="text"
                v-model="selectedCheckWorkflow.name" 
                :placeholder="$t('wam.clone.name')"
                :label="$t('wam.clone.name')"
                validation="bail|required|max:30,length|matches:/^[a-zA-Z0-9 ]*[a-zA-Z0-9]+[a-zA-Z0-9 ]*$/"
                :element-class="
                  (context, classes) => ['flex-1 min-w-full'].concat(classes)
                "
                :validation-messages="{
                  matches: $t('general.allowed_chars_error'),
                }"
                error-behavior="value"
                @validation="validationName"
                class="formulate-lg"
              />
              
              <FormulateInput
                type="text"
                v-model="selectedCheckWorkflow.description"
                :placeholder="$t('wam.clone.description')"
                :label="$t('wam.clone.description')"
                class="formulate-lg"
                :validation="selectedCheckWorkflow.description ? `bail|max:100,length|matches:/^[a-zA-Z0-9 ]*[a-zA-Z0-9]+[a-zA-Z0-9 ]*$/` : ``"
                :validation-messages="{
                  matches: $t('general.allowed_chars_error'),
                }"
                :element-class="
                  (context, classes) => ['flex-1 min-w-full'].concat(classes)
                "
                error-behavior="value"
                @validation="validationDescription"
              />

              <div class="flex justify-end mt-6">
                <button
                  role="button"
                  class="px-4 py-2 h-10 w-24 font-medium rounded-md ml-auto text-center"
                  :class="!hasErrors ? 'bg-blue-700 text-white cursor-pointer' : 'bg-gray-300 text-gray-500 cursor-not-allowed pointer-events-none'"
                  @click="handleCloneModal()"
                  :disabled="hasErrors"
                >
                  {{ $t('wam.clone.clone') }}
                </button>
              </div>
            </FormulateForm>
          </div>
        </div>
      </template>
    </modalContent>
  </div>
</template>

<script>
import SubHeader from "@/components/SubHeader";
import Table from "@shared/dashboard-components/table-large";
import { fetchStatusList, cloneCheckWorkflow } from "@shared/workflow/service.js";
import { FormatDateNew } from "@/plugins/functions";
import { checkPermission } from "@shared/utils/functions";
import { getFilterOptions, getFilteredRows } from "@shared/dashboard-components/table-large/utils";
import { paginationOptions } from "@shared/dashboard-components/table-large/utils/constants";
import { caseEntityTypesMap } from "@shared/modules/localization/utils/caseEntityTranslation/constants";
import { translateEntity } from "@shared/modules/localization/utils/caseEntityTranslation";
import modalContent from "@/components/modal-content";
import handleError from "@shared/mixins/handleError";
import { alphanumericSpacesHyphensUnderscoreRegex } from "@shared/utils/validation/regex.js";

// import  Vue  from "vue";

export default {
  name: "workflowDetail",
  mixins: [handleError],

  components: {
    SubHeader,
    "v-table": Table,
    modalContent,
  },
  data() {
    return {
      alphanumericSpacesHyphensUnderscoreRegex,
      isLoading: false,
      listLoader: false,
      statusList: [],
      paginationOptions: { ...paginationOptions },
      totalRows: 0,
      filterTerm: {},
      selectedCheckWorkflow:{
        name: "",
        description: "",
      },
      cloningCheckWorkflow: null,
      nameHasError: false,
      descriptionHasError: false,
    };
  },
  mounted() {
    this.getStatusList();
  },
  computed: {

      columns() { 
        return [
          {
            label: this.$t('pages.workflow_engine.action'),
            field: "actions",
            config: { filter: false, type: "actions" },
            sortable: false,
            tdClass: "w-10",
          },
          {
            label: this.$t('pages.workflow_engine.workflow_name'),
            field: "name",
            sortable: true,
            config: {
              filter: {
                nameModifier: (el) => this.translateEntity(el).name,
              },
              type: "select",
              showToolTip: true,
            },
            tdClass: "truncate max-w-sm",
          },
          {
            label: this.$t('pages.workflow_engine.description'),
            field: "description",
            sortable: true,
            //   query_key: "check_type",
            config: {
              filter: {
                nameModifier: (el) => this.translateEntity(el).description,
              },
              type: "select",
              showToolTip: true,
            },
            tdClass: "truncate max-w-sm",
          },
          // {
          //   label: "Package Name",
          //   field: "packageName",
          //   sortable: true,
          //   config: { filter: true, type: "select" },
          //   tdClass: "truncate max-w-sm",
          // },
          {
            label: this.$t('pages.workflow_engine.created_at'),
            field: "created_at",
            sortable: true,
            config: { filter: true, type: "source" },
            tdClass: "truncate max-w-sm",
          },
          {
            label: this.$t('pages.workflow_engine.last_updated_by'),
            field: "modified_by",
            sortable: true,
            //   config: { filter: false, type: "show_info" },
            tdClass: "truncate max-w-sm",
          },
          {
            label: this.$t('pages.workflow_engine.last_updated_at'),
            field: "modified_at",
            sortable: true,
            config: { filter: true, type: "select" },
            tdClass: "truncate max-w-sm",
          },
        ]
    },
    filteredRows() {
      return getFilteredRows(this.statusList, this.filterTerm);
    },
    translatedItems() {
      return this.filteredRows.map(this.translateEntity);
    },
    showCreateButton() {
      return checkPermission("workflow.create");
    },
    getTotalRows() {
      return this.filteredRows.length;
    },
    filterOptions() {
      let result = this.getFilterOptions(this.columns, this.statusList);
      return result;
    },
  },
  methods: {
    FormatDateNew,
    checkPermission,
    getFilterOptions,
    translateEntity(entity) {
      return translateEntity(entity, caseEntityTypesMap.WORKFLOW);
    },
    async getStatusList() {
      try {
        this.listLoader = true;
        const response = await fetchStatusList("check");
        if (response.data.status) {
          this.statusList = response?.data?.data || [];
          this.statusList = this.statusList?.map((item) => {
            if (item.created_at || item.modified_at) {
              item.created_at = FormatDateNew(new Date(item.created_at));
              item.modified_at = FormatDateNew(new Date(item.modified_at));
              return item;
            }
          });
          this.totalRows = this.statusList.length;
          this.listLoader = false;
        }
      } catch (err) {
        console.log("err", err);
        this.listLoader = false;
        this.$toast.error(
          err?.response?.data?.detail || this.$t('general.default_error')
        );
      }
    },
    createCaseWorkflow() {
      this.$router.push({
        name: "Workflow Engine Details",
        params: { action: "create", type: "check" },
      });
      return;
    },
    onColumnFilters({ columnFilters }) {
      this.filterTerm = columnFilters;
      this.paginationOptions = { ...this.paginationOptions, setCurrentPage: 1 };
    },
    clearFilters() {
      this.filterTerm = {};
      this.getStatusList();
    },
    onPageChange(evt) {
      this.paginationOptions.setCurrentPage = evt.currentPage;
    },
    async handleCloneWorkflow(workflow) {
      this.$refs["clone-check-workflow-modal"].showModal();
      this.cleanCloneInput();
      this.cloningCheckWorkflow = workflow;      
    },
    closeCheckWorkFlow(){
      this.cleanCloneInput()
      this.$refs["clone-check-workflow-modal"].hideModal();
    },
    async handleCloneModal(){
      try {
        let payload = this.selectedCheckWorkflow;
        if(!this.selectedCheckWorkflow.description) {
          delete payload.description;
        }
        await cloneCheckWorkflow(this.cloningCheckWorkflow.id, payload)
        this.$toast.success(this.$t("wam.clone.cloned_message"));
        this.closeCheckWorkFlow();
        this.getStatusList();
      }catch (error) {
        this.handleError(error);
      } 
    },
     cleanCloneInput(){
        this.selectedCheckWorkflow.name = "";
        this.selectedCheckWorkflow.description = "";
        this.cloningCheckWorkflow= null
    },
    validationName(evt) {
      this.nameHasError = evt.hasErrors;
    },
    validationDescription(evt) {
      this.descriptionHasError = evt.hasErrors;
    },
  },
};
</script>
